"use client";

import {
  addWeeks,
  format,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
} from "date-fns";
import { srLatn } from "date-fns/locale";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";

import { Calendar } from "~/components/ui/custom-calendar";
import { Skeleton } from "~/components/ui/skeleton";
import { getTimeSlotsUrl } from "~/lib/time-slots-url";
import { api } from "~/trpc/react";

type HomepageAvailabilityPreviewProps = {
  initialDateIsos?: readonly string[];
};

const EMPTY_INITIAL_DATE_ISOS: readonly string[] = [];

export function HomepageAvailabilityPreview({
  initialDateIsos = EMPTY_INITIAL_DATE_ISOS,
}: HomepageAvailabilityPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isActivated, setIsActivated] = useState(false);
  const [clientSeed, setClientSeed] = useState<{
    initialDateIso: string;
    timezone: string;
  }>();

  useEffect(() => {
    if (isActivated) return;

    const container = containerRef.current;
    if (!container) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsActivated(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        setIsActivated(true);
        observer.disconnect();
      },
      { rootMargin: "320px 0px" },
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [isActivated]);

  useEffect(() => {
    if (!isActivated) return;

    const now = new Date();
    const nowMs = now.getTime();
    const initialDateIso = initialDateIsos
      .filter((iso) => {
        const timestamp = Date.parse(iso);

        return Number.isFinite(timestamp) && timestamp > nowMs;
      })
      .sort((a, b) => Date.parse(a) - Date.parse(b))[0];

    setClientSeed({
      initialDateIso: initialDateIso ?? now.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    });
  }, [initialDateIsos, isActivated]);

  const activate = () => setIsActivated(true);

  return (
    <div
      ref={containerRef}
      className="flex flex-1 flex-col"
      onFocusCapture={activate}
      onPointerDownCapture={activate}
    >
      {clientSeed ? (
        <HomepageAvailabilityPreviewWithData
          initialDateIso={clientSeed.initialDateIso}
          timezone={clientSeed.timezone}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
          Slobodni termini će se pojaviti ovde.
        </div>
      )}
    </div>
  );
}

function HomepageAvailabilityPreviewWithData({
  initialDateIso,
  timezone,
}: {
  initialDateIso: string;
  timezone: string;
}) {
  const initialDateKey = useMemo(
    () => formatInTimeZone(new Date(initialDateIso), timezone, "yyyy-MM-dd"),
    [initialDateIso, timezone],
  );
  const initialDate = useMemo(
    () => new Date(`${initialDateKey}T12:00:00`),
    [initialDateKey],
  );
  const referenceDate = startOfDay(initialDate);
  const { queryStartDate, queryEndDate } = getAvailabilityQueryBounds(
    initialDateKey,
    timezone,
  );
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const dailySummaryQuery =
    api.therapistRouter.getDailyAvailabilitySummary.useQuery({
      startDate: queryStartDate,
      endDate: queryEndDate,
      timezone,
      sessionType: "INDIVIDUAL",
    });

  const hourlyAvailabilityQuery =
    api.therapistRouter.getHourlyAvailability.useQuery({
      date: selectedDate.toISOString(),
      timezone,
      sessionType: "INDIVIDUAL",
    });

  const { data: dailySummary } = dailySummaryQuery;
  const {
    data: hourlyAvailability,
    isError: hasHourlyAvailabilityError,
    isLoading: isLoadingSlots,
  } = hourlyAvailabilityQuery;

  const availableTimeslots = useMemo(() => {
    if (!dailySummary) return {};

    return Object.entries(dailySummary).reduce<Record<string, Date[]>>(
      (datesByDay, [dateKey, hasAvailability]) => {
        if (hasAvailability) {
          datesByDay[dateKey] = [new Date(`${dateKey}T12:00:00`)];
        }
        return datesByDay;
      },
      {},
    );
  }, [dailySummary]);

  useEffect(() => {
    if (!dailySummary) return;

    const selectedKey = format(selectedDate, "yyyy-MM-dd");
    if (dailySummary[selectedKey]) return;

    const firstAvailableKey = Object.keys(dailySummary)
      .sort()
      .find((dateKey) => dailySummary[dateKey]);

    if (firstAvailableKey) {
      setSelectedDate(new Date(`${firstAvailableKey}T12:00:00`));
    }
  }, [dailySummary, selectedDate]);

  const timeOptions = useMemo(() => {
    return (hourlyAvailability ?? [])
      .filter(({ availableTherapists }) => availableTherapists > 0)
      .map(({ hour, minute, availableTherapists }) => ({
        label: `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`,
        therapistCount: availableTherapists,
      }));
  }, [hourlyAvailability]);

  const previewTimeOptions = useMemo(
    () => pickPreviewTimeOptions(timeOptions),
    [timeOptions],
  );

  return (
    <div className="flex flex-1 flex-col">
      <Calendar
        density="compact"
        selected={selectedDate}
        onSelect={(date) => {
          setSelectedDate(date);
        }}
        fromDate={referenceDate}
        availableTimeslots={availableTimeslots}
      />

      {dailySummaryQuery.isError && (
        <AvailabilityError
          message="Trenutno ne možemo da učitamo slobodne dane."
          onRetry={() => void dailySummaryQuery.refetch()}
        />
      )}

      <div className="mt-auto border-t border-border px-3 py-3">
        <p className="text-xs text-muted-foreground">
          Vreme za {format(selectedDate, "d. MMMM", { locale: srLatn })}
        </p>
        <div className="mt-2 grid min-h-13 grid-cols-4 gap-2">
          {isLoadingSlots && (
            <>
              <Skeleton className="h-13 rounded-md" />
              <Skeleton className="h-13 rounded-md" />
              <Skeleton className="h-13 rounded-md" />
              <Skeleton className="h-13 rounded-md" />
            </>
          )}
          {!isLoadingSlots &&
            !hasHourlyAvailabilityError &&
            previewTimeOptions.map(({ label, therapistCount }) => (
              <Link
                key={label}
                href={getTimeSlotsUrl(setTimeOnDate(selectedDate, label))}
                className="rounded-md border border-border bg-background px-2 py-2 text-center transition-colors hover:border-brand/40 hover:bg-secondary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                <span className="block text-sm font-semibold">{label}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {therapistCount}{" "}
                  {therapistCount === 1 ? "terapeut" : "terapeuta"}
                </span>
              </Link>
            ))}
          {hasHourlyAvailabilityError && (
            <div className="col-span-4 self-center">
              <AvailabilityError
                message="Trenutno ne možemo da učitamo slobodna vremena za ovaj datum."
                onRetry={() => void hourlyAvailabilityQuery.refetch()}
                compact
              />
            </div>
          )}
          {!isLoadingSlots &&
            !hasHourlyAvailabilityError &&
            previewTimeOptions.length === 0 && (
              <p className="col-span-4 self-center text-xs text-muted-foreground">
                Pogledaj sva dostupna vremena za ovaj datum.
              </p>
            )}
        </div>
      </div>
    </div>
  );
}

export function getAvailabilityQueryBounds(dateKey: string, timezone: string) {
  const endDateKey = format(
    addWeeks(new Date(`${dateKey}T12:00:00`), 4),
    "yyyy-MM-dd",
  );

  return {
    queryStartDate: fromZonedTime(
      `${dateKey}T00:00:00`,
      timezone,
    ).toISOString(),
    queryEndDate: fromZonedTime(
      `${endDateKey}T00:00:00`,
      timezone,
    ).toISOString(),
  };
}

function AvailabilityError({
  message,
  onRetry,
  compact = false,
}: {
  message: string;
  onRetry: () => void;
  compact?: boolean;
}) {
  return (
    <div
      role="alert"
      className={
        compact ? "text-xs" : "border-t border-border px-3 py-3 text-xs"
      }
    >
      <p className="text-muted-foreground">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 font-semibold text-brand underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand"
      >
        Pokušaj ponovo
      </button>
    </div>
  );
}

function setTimeOnDate(date: Date, time: string) {
  const [hoursText, minutesText] = time.split(":");

  return setMilliseconds(
    setSeconds(
      setMinutes(
        setHours(date, Number(hoursText ?? 0)),
        Number(minutesText ?? 0),
      ),
      0,
    ),
    0,
  );
}

function pickPreviewTimeOptions(
  options: Array<{ label: string; therapistCount: number }>,
) {
  const targets = [9, 13, 17, 20];
  const remaining = [...options];

  return targets.flatMap((target) => {
    const closestIndex = remaining.reduce((bestIndex, option, index) => {
      const hour = Number(option.label.slice(0, 2));
      const bestHour = Number(remaining[bestIndex]?.label.slice(0, 2));

      return Math.abs(hour - target) < Math.abs(bestHour - target)
        ? index
        : bestIndex;
    }, 0);
    const [closest] = remaining.splice(closestIndex, 1);

    return closest ? [closest] : [];
  });
}
